// Talking NPCs, client side: the owner's talk credits, and a visitor's
// conversation with one NPC (functions/api/npc/talk.ts, listen.ts).
import { supabase } from '../config/supabase';
import { gathrxrAuthHeader } from '../utils/quotaHeaders';

export interface TalkCredits {
  signedIn: boolean;
  admin: boolean;
  remaining: number | null; // null = unlimited (admin)
  used: number;
  packUnits: number;
}

export async function fetchTalkCredits(): Promise<TalkCredits | null> {
  const { data, error } = await supabase.rpc('npc_talk_access');
  if (error || !data) return null;
  const d = data as any;
  return {
    signedIn: !!d.signed_in,
    admin: !!d.admin,
    remaining: d.admin ? null : (d.remaining ?? 0),
    used: d.used ?? 0,
    packUnits: d.pack_units ?? 500,
  };
}

export interface ChatLine { role: 'user' | 'assistant'; content: string }
export interface TalkReply {
  reply: string | null;
  audio: string | null; // base64 mp3
  /** The owner has run out of talk credits: `reply` is the NPC's resting line. */
  resting?: boolean;
}

export class TalkError extends Error {}

/** Can this NPC talk right now (enabled, and the owner has credits)? */
export async function npcCanTalk(worldId: string, npcId: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/npc/talk?worldId=${encodeURIComponent(worldId)}&npcId=${encodeURIComponent(npcId)}`, {
      headers: await gathrxrAuthHeader(),
    });
    if (!res.ok) return false;
    return !!(await res.json()).ok;
  } catch {
    return false;
  }
}

export async function talkToNpc(worldId: string, npcId: string, messages: ChatLine[], greet = false): Promise<TalkReply> {
  const res = await fetch('/api/npc/talk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await gathrxrAuthHeader()) },
    body: JSON.stringify({ worldId, npcId, messages, greet }),
  });
  const data: any = await res.json().catch(() => ({}));
  if (res.status === 402) return { reply: data.reply || null, audio: null, resting: true };
  if (!res.ok) throw new TalkError(data.error || 'They didn’t catch that. Try again.');
  return { reply: data.reply ?? null, audio: data.audio ?? null };
}

/** Hold-to-talk clip → text. */
export async function listenToVisitor(worldId: string, npcId: string, clip: Blob): Promise<string> {
  const form = new FormData();
  form.append('file', clip, clip.type.includes('mp4') ? 'clip.mp4' : 'clip.webm');
  form.append('worldId', worldId);
  form.append('npcId', npcId);
  const res = await fetch('/api/npc/listen', { method: 'POST', headers: await gathrxrAuthHeader(), body: form });
  const data: any = await res.json().catch(() => ({}));
  if (res.status === 402) throw new TalkError('They’re resting right now.');
  if (!res.ok) throw new TalkError(data.error || 'Couldn’t hear that. Try again or type it.');
  return String(data.text || '');
}
